import dayjs from 'dayjs'
import { ChevronDown } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { SelectFormComponent } from '@/modules/shared/components/select-form.component'
import { Button } from '@/modules/shared/components/ui/button'
import { Card, CardContent } from '@/modules/shared/components/ui/card'
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '@/modules/shared/components/ui/dropdown-menu'
import { Form } from '@/modules/shared/components/ui/form'
import { Input } from '@/modules/shared/components/ui/input'
import { useMainStore } from '@/modules/shared/stores/main.store'
import { priceFormatter } from '@/modules/shared/utils/formatter'

import { SellerCardComponent } from '../components/seller-card.component'
import { SellersReportTable } from '../components/sellers-report-table/seller-report-table'
import { getManagerReportService } from '../services/get-manager-report.service'
import type { SellerReportType } from '../types/seller-report.type'

const months = [
	{ id: '01', name: 'Janeiro' },
	{ id: '02', name: 'Fevereiro' },
	{ id: '03', name: 'Março' },
	{ id: '04', name: 'Abril' },
	{ id: '05', name: 'Maio' },
	{ id: '06', name: 'Junho' },
	{ id: '07', name: 'Julho' },
	{ id: '08', name: 'Agosto' },
	{ id: '09', name: 'Setembro' },
	{ id: '10', name: 'Outubro' },
	{ id: '11', name: 'Novembro' },
	{ id: '12', name: 'Dezembro' },
]

export type SearchFormData = {
	month: string
	year: string
}

export function ManageSellersInterface() {
	const [representantes, setRepresentantes] = useState<SellerReportType[]>([])
	const [orderedByColumn, setOrderedByColumn] = useState<string>('faturamento')

	const form = useForm<SearchFormData>({
		defaultValues: {
			month: dayjs().format('MM'),
			year: dayjs().format('YYYY'),
		},
	})

	const {
		isLoading,
		actions: { setIsLoading },
	} = useMainStore()

	const handleSearch = async (data: SearchFormData) => {
		setIsLoading(true)
		try {
			const response = await getManagerReportService({
				startDate: dayjs(`${data.year}-${data.month}-01`)
					.toISOString()
					.split('T')[0],
				endDate: `${new Date(Number.parseInt(data.year), Number.parseInt(data.month), 0).toISOString().split('T')[0]}`,
			})

			setRepresentantes(JSON.parse(response))
			setFilteredRepresentantes(JSON.parse(response))
		} catch (error) {
			toast.info('Não foi possível carregar os dados')
		} finally {
			setIsLoading(false)
		}
	}

	function getYears() {
		return Array.from({ length: 5 }).map(
			(_, index) => new Date().getFullYear() - index,
		)
	}

	const watchedMonth = form.watch('month')
	const watchedYear = form.watch('year')

	const [filteredRepresentantes, setFilteredRepresentantes] = useState<
		SellerReportType[]
	>([])
	const handleFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value
		const filteredRepresentantes = representantes.filter((representante) =>
			representante.razaoSocialNome.toLowerCase().includes(value.toLowerCase()),
		)
		setFilteredRepresentantes(filteredRepresentantes)
	}

	const totalAnual = priceFormatter
		.format(representantes?.reduce((acc, cur) => acc + (cur.anual || 0), 0))
		.replace('R$', '')
	const totalTrimestral = priceFormatter
		.format(
			representantes?.reduce((acc, cur) => acc + (cur.trimestral || 0), 0),
		)
		.replace('R$', '')
	const totalMensal = priceFormatter
		.format(representantes?.reduce((acc, cur) => acc + (cur.mensal || 0), 0))
		.replace('R$', '')
	const totalVendas = priceFormatter
		.format(representantes?.reduce((acc, cur) => acc + (cur.vendas || 0), 0))
		.replace('R$', '')
	const totalFaturamento = priceFormatter
		.format(
			representantes?.reduce((acc, cur) => acc + (cur.faturamento || 0), 0),
		)
		.replace('R$', '')

	const arrayCardDetails = [
		{ title: 'Anual', value: totalAnual },
		{ title: 'Trimestral', value: totalTrimestral },
		{ title: 'Mensal', value: totalMensal },
		{ title: 'Vendas', value: totalVendas },
		{ title: 'Faturamento', value: totalFaturamento },
	]

	const columns = [
		{
			id: 'anual',
			Header: 'Cota Anual',
		},
		{
			id: 'trimestral',
			Header: 'Cota Trimestral',
		},
		{
			id: 'mensal',
			Header: 'Cota Mensal',
		},
		{
			id: 'vendas',
			Header: 'Vendas',
		},
		{
			id: 'desvio',
			Header: 'Desvio',
		},
		{
			id: 'faturamento',
			Header: 'Faturamento',
		},
		{
			id: 'participacao',
			Header: 'Participação',
		},
	]

	const orderByColumn = (column: string) => {
		if (orderedByColumn === column) {
			setOrderedByColumn('faturamento')
			return
		}

		setOrderedByColumn(column)

		const sorted = [...filteredRepresentantes].sort((a, b) => {
			const aValue = a[column]
			const bValue = b[column]

			if (typeof aValue === 'number' && typeof bValue === 'number') {
				return bValue - aValue // Inverte a comparação para ordem decrescente
			}

			if (typeof aValue === 'string' && typeof bValue === 'string') {
				return bValue.localeCompare(aValue) // Inverte a comparação para ordem decrescente
			}

			return 0 // Não altera a ordem
		})

		setFilteredRepresentantes(sorted)
	}

	useEffect(() => {
		if (!watchedMonth || !watchedYear || isLoading) return
		handleSearch(form.getValues())
	}, [watchedMonth, watchedYear])

	return (
		<div>
			<Form {...form}>
				<div className="flex items-end gap-2 mb-2">
					<Input
						placeholder="Pesquisar"
						className="w-full md:max-w-[50%]"
						onChange={handleFilter}
					/>
					<SelectFormComponent<SearchFormData>
						control={form.control}
						name="month"
						label="Mês"
						options={months.map((item) => ({
							label: item.name,
							value: item.id.toString(),
						}))}
					/>

					<SelectFormComponent<SearchFormData>
						control={form.control}
						name="year"
						label="Ano"
						defaultValue={new Date().getFullYear().toString()}
						options={getYears().map((item) => ({
							label: item.toString(),
							value: item.toString(),
						}))}
					/>
				</div>

				<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1">
					{arrayCardDetails.map((item, index) => (
						<Card key={item.title} className="p-2">
							<CardContent className="p-2">
								<div className="text-sm font-semibold text-nowrap">
									{item.title}
								</div>
								<div className="text-xs">{item.value}</div>
							</CardContent>
						</Card>
					))}
				</div>

				<Card className=" hidden lg:block  lg:w-full mt-2">
					<CardContent className="mt-2">
						<SellersReportTable data={filteredRepresentantes} />
					</CardContent>
				</Card>

				<div className=" space-y-1 lg:hidden">
					<div className="flex flex-1 justify-end mb-2 mt-2">
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button size="sm">
									Ordenar por
									<ChevronDown className="ml-2 h-3 w-3" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								{columns.map((column) => (
									<DropdownMenuCheckboxItem
										checked={column.id === orderedByColumn}
										key={column.id}
										onClick={() => orderByColumn(column.id)}
									>
										{column.Header}
									</DropdownMenuCheckboxItem>
								))}
							</DropdownMenuContent>
						</DropdownMenu>
					</div>

					<div className="grid gap-1 grid-cols-1 sm:grid-cols-2">
						{filteredRepresentantes.map((representante, index) => (
							<SellerCardComponent
								key={representante.razaoSocialNome}
								sellerReport={representante}
							/>
						))}
					</div>
				</div>
			</Form>
		</div>
	)
}
